import CaseReferredByCompanyAutocomplete from "@/components/Forms/CaseReferredByCompanySelect.tsx";
import CaseReferredByIndividualAutocomplete from "@/components/Forms/CaseReferredByIndividualSelect.tsx";
import CaseStatusesSelectMultiple from "@/components/Forms/CaseStatusesSelectMultiple.tsx";
import EmployeeByTypeMultiple from "@/components/Forms/EmployeeByTypeMultiple.tsx";
import MultiSelect from "@/components/Forms/MultiSelect.tsx";
import {
    umUimCaseAccepted,
    yesNoBooleanOptionsWithBlank,
    yesNoOptions,
    yesNoOptionsWithNone,
} from "@/components/Table/Filters/AdvancedFilters/AccidentFields.tsx";
import { attorneyFeeOptionsWithNone } from "@/constants/attorneyFeeOptions.ts";
import { governmentEntityTypes } from "@/constants/governmentEntityTypes.ts";
import { leadStatusOptionsWithNone } from "@/constants/leadStatusOptions.ts";
import { optionsLiability } from "@/constants/optionsLiability.ts";
import { optionsLitigationSubStatusWithNone } from "@/constants/optionsLitigationSubStatus.ts";
import { useFetchTeams } from "@/queries/useFetchTeam.ts";
import { Box, CircularProgress, Grid2, MenuItem, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { RhfTextField } from "mui-rhf-integration";
import type React from "react";
import { Controller, useFormContext } from "react-hook-form";
import LiabilitySelectMultiple from "./LiabilitySelectMultiple.tsx";

export const DateRangeWrapper = styled(Box)(({ theme }) => ({
    gridColumn: "1 / -1",
    marginBottom: theme.spacing(3),
}));

export const DateRangeLabel = styled(Typography)(({ theme }) => ({
    fontWeight: 500,
    marginBottom: theme.spacing(2),
    color: theme.palette.text.primary,
}));

export const DateRangeContainer = styled(Box)(({ theme }) => ({
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: theme.spacing(2),
}));

const sharedTextFieldSx = {
    "& .MuiInputBase-root": {
        height: 56,
    },
};

const sharedSlotProps = {
    select: {
        MenuProps: {
            sx: {
                "& .MuiMenuItem-root": {
                    minHeight: 40,
                },
            },
        },
    },
};

const DateRangeFields: React.FC = () => {
    const { control } = useFormContext();
    const { data: teamsData, isLoading: teamsLoading } = useFetchTeams();

    const teams =
        teamsData?.map((team) => ({
            value: team.teamName || "",
            label: team.teamName || "",
        })) || [];

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DateRangeWrapper>
                <DateRangeLabel>Created Date</DateRangeLabel>
                <DateRangeContainer>
                    <Controller
                        name="createdDateFrom"
                        control={control}
                        render={({ field }) => (
                            <DatePicker
                                label="From"
                                value={field.value}
                                onChange={field.onChange}
                                slotProps={{ textField: { fullWidth: true } }}
                            />
                        )}
                    />
                    <Controller
                        name="createdDateTo"
                        control={control}
                        render={({ field }) => (
                            <DatePicker
                                label="To"
                                value={field.value}
                                onChange={field.onChange}
                                slotProps={{ textField: { fullWidth: true } }}
                            />
                        )}
                    />
                </DateRangeContainer>
            </DateRangeWrapper>
            <DateRangeWrapper>
                <DateRangeLabel>Date of Incident (DOI)</DateRangeLabel>
                <DateRangeContainer>
                    <Controller
                        name="doiFrom"
                        control={control}
                        render={({ field }) => (
                            <DatePicker
                                label="From"
                                value={field.value}
                                onChange={field.onChange}
                                slotProps={{ textField: { fullWidth: true } }}
                            />
                        )}
                    />
                    <Controller
                        name="doiTo"
                        control={control}
                        render={({ field }) => (
                            <DatePicker
                                label="To"
                                value={field.value}
                                onChange={field.onChange}
                                slotProps={{ textField: { fullWidth: true } }}
                            />
                        )}
                    />
                </DateRangeContainer>
            </DateRangeWrapper>

            <DateRangeWrapper>
                <DateRangeLabel>SOL</DateRangeLabel>
                <DateRangeContainer>
                    <Controller
                        name="solFrom"
                        control={control}
                        render={({ field }) => (
                            <DatePicker
                                label="From"
                                value={field.value}
                                onChange={field.onChange}
                                slotProps={{ textField: { fullWidth: true } }}
                            />
                        )}
                    />
                    <Controller
                        name="solTo"
                        control={control}
                        render={({ field }) => (
                            <DatePicker
                                label="To"
                                value={field.value}
                                onChange={field.onChange}
                                slotProps={{ textField: { fullWidth: true } }}
                            />
                        )}
                    />
                </DateRangeContainer>
            </DateRangeWrapper>

            <Grid2 container spacing={2}>
                <Grid2 size={6}>
                    <MultiSelect
                        name="anteLitemCase"
                        label="Ante Litem Case"
                        options={yesNoOptions}
                    />
                </Grid2>
                <Grid2 size={6}>
                    <MultiSelect
                        name="governmentEntity"
                        label="Government Entity"
                        options={governmentEntityTypes.map((option) => ({
                            value: option.id,
                            label: option.label,
                        }))}
                    />
                </Grid2>
            </Grid2>

            <DateRangeWrapper sx={{ mt: 3 }}>
                <DateRangeLabel>Ante Litem Sent On</DateRangeLabel>
                <DateRangeContainer>
                    <Controller
                        name="anteLitemSentFrom"
                        control={control}
                        render={({ field }) => (
                            <DatePicker
                                label="From"
                                value={field.value}
                                onChange={field.onChange}
                                slotProps={{ textField: { fullWidth: true } }}
                            />
                        )}
                    />
                    <Controller
                        name="anteLitemSentTo"
                        control={control}
                        render={({ field }) => (
                            <DatePicker
                                label="To"
                                value={field.value}
                                onChange={field.onChange}
                                slotProps={{ textField: { fullWidth: true } }}
                            />
                        )}
                    />
                </DateRangeContainer>
            </DateRangeWrapper>

            <Grid2 container spacing={2}>
                <Grid2 size={6}>
                    <LiabilitySelectMultiple name="liabilityStatus" label="Liability Status" />
                </Grid2>
                <Grid2 size={6}>
                    <MultiSelect
                        name="liabilityAccepted"
                        label="Liability Accepted"
                        options={optionsLiability
                            .filter((option) => option.id)
                            .map((option) => ({
                                value: option.id as string,
                                label: option.label,
                            }))}
                    />
                </Grid2>
            </Grid2>

            <DateRangeWrapper sx={{ mt: 3 }}>
                <DateRangeLabel>Liability Demand Sent On</DateRangeLabel>
                <DateRangeContainer>
                    <Controller
                        name="lbtyDemandSentFrom"
                        control={control}
                        render={({ field }) => (
                            <DatePicker
                                label="From"
                                value={field.value}
                                onChange={field.onChange}
                                slotProps={{ textField: { fullWidth: true } }}
                            />
                        )}
                    />
                    <Controller
                        name="lbtyDemandSentTo"
                        control={control}
                        render={({ field }) => (
                            <DatePicker
                                label="To"
                                value={field.value}
                                onChange={field.onChange}
                                slotProps={{ textField: { fullWidth: true } }}
                            />
                        )}
                    />
                </DateRangeContainer>
            </DateRangeWrapper>

            <Grid2 container spacing={2}>
                <Grid2 size={6}>
                    <RhfTextField
                        select
                        slotProps={sharedSlotProps}
                        name="policyLimits"
                        control={control}
                        label="Policy Limits"
                        fullWidth
                        sx={sharedTextFieldSx}
                    >
                        {yesNoBooleanOptionsWithBlank.map((option) => (
                            <MenuItem key={option.label} value={String(option.value)}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </RhfTextField>
                </Grid2>
                <Grid2 size={6}>
                    <RhfTextField
                        select
                        slotProps={sharedSlotProps}
                        name="medpay"
                        control={control}
                        label="Medpay"
                        fullWidth
                        sx={sharedTextFieldSx}
                    >
                        {yesNoBooleanOptionsWithBlank.map((option) => (
                            <MenuItem key={option.label} value={String(option.value)}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </RhfTextField>
                </Grid2>
            </Grid2>

            <DateRangeWrapper sx={{ mt: 3 }}>
                <DateRangeLabel>ERISA Date Sent</DateRangeLabel>
                <DateRangeContainer>
                    <Controller
                        name="erisaDateSentFrom"
                        control={control}
                        render={({ field }) => (
                            <DatePicker
                                label="From"
                                value={field.value}
                                onChange={field.onChange}
                                slotProps={{ textField: { fullWidth: true } }}
                            />
                        )}
                    />
                    <Controller
                        name="erisaDateSentTo"
                        control={control}
                        render={({ field }) => (
                            <DatePicker
                                label="To"
                                value={field.value}
                                onChange={field.onChange}
                                slotProps={{ textField: { fullWidth: true } }}
                            />
                        )}
                    />
                </DateRangeContainer>
            </DateRangeWrapper>

            <Grid2 container spacing={2}>
                <Grid2 size={6}>
                    <CaseStatusesSelectMultiple
                        name={"idCaseStatusUM"}
                        label={"UM/UIM Case Status"}
                    />
                </Grid2>
                <Grid2 size={6}>
                    <MultiSelect
                        name="uMUIMAccepted"
                        label="UM/UIM Accepted"
                        options={umUimCaseAccepted}
                    />
                </Grid2>
            </Grid2>

            <DateRangeWrapper sx={{ mt: 3 }}>
                <DateRangeLabel>UM Demand Sent On</DateRangeLabel>
                <DateRangeContainer>
                    <Controller
                        name="umDemandSentFrom"
                        control={control}
                        render={({ field }) => (
                            <DatePicker
                                label="From"
                                value={field.value}
                                onChange={field.onChange}
                                slotProps={{ textField: { fullWidth: true } }}
                            />
                        )}
                    />
                    <Controller
                        name="umDemandSentTo"
                        control={control}
                        render={({ field }) => (
                            <DatePicker
                                label="To"
                                value={field.value}
                                onChange={field.onChange}
                                slotProps={{ textField: { fullWidth: true } }}
                            />
                        )}
                    />
                </DateRangeContainer>
            </DateRangeWrapper>

            <Grid2 container spacing={2}>
                <Grid2 size={4}>
                    {teamsLoading ? (
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                height: 56,
                            }}
                        >
                            <CircularProgress size={24} />
                        </Box>
                    ) : (
                        <MultiSelect name="assignedTeam" label="Team" options={teams} />
                    )}
                </Grid2>
                <Grid2 size={4}>
                    <EmployeeByTypeMultiple
                        label={"Attorney"}
                        name={"assignedToAttorney"}
                        type={"Attorney"}
                        includeInactive={true}
                    />
                </Grid2>
                <Grid2 size={4}>
                    <EmployeeByTypeMultiple
                        label={"Disbursement Specialist"}
                        name={"assignedToDisbursementSpecialist"}
                        type={"DisbursementSpecialist"}
                        includeInactive={true}
                    />
                </Grid2>
            </Grid2>

            <Grid2 container spacing={2} sx={{ mt: 3 }}>
                <Grid2 size={4}>
                    <EmployeeByTypeMultiple
                        label={"Intake Specialist"}
                        name={"assignedToIntakeSpecialist"}
                        type={"IntakeSpecialist"}
                        includeInactive={true}
                    />
                </Grid2>
                <Grid2 size={4}>
                    <EmployeeByTypeMultiple
                        label={"Case Manager"}
                        name={"assignedToParalegal"}
                        type={"CaseManager"}
                        includeInactive={true}
                    />
                </Grid2>
                <Grid2 size={4}>
                    <EmployeeByTypeMultiple
                        label={"Medical Records Clerk"}
                        name={"assignedToMedicalRecordsClerk"}
                        type={"MedicalRecordsClerk"}
                        includeInactive={true}
                    />
                </Grid2>
            </Grid2>

            <Grid2 container spacing={2} sx={{ mt: 3 }}>
                <Grid2 size={4}>
                    <RhfTextField
                        select
                        slotProps={sharedSlotProps}
                        name="financeLoan"
                        control={control}
                        label="Finance Loan"
                        fullWidth
                        sx={sharedTextFieldSx}
                    >
                        {yesNoOptionsWithNone.map((option) => (
                            <MenuItem key={option.label} value={String(option.value)}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </RhfTextField>
                </Grid2>
                <Grid2 size={4}>
                    <RhfTextField
                        select
                        slotProps={sharedSlotProps}
                        name="attorneyFee"
                        control={control}
                        label="Attorney Fee %"
                        fullWidth
                        sx={sharedTextFieldSx}
                    >
                        {attorneyFeeOptionsWithNone.map((value) => (
                            <MenuItem key={value} value={String(value)}>
                                {value}
                            </MenuItem>
                        ))}
                    </RhfTextField>
                </Grid2>
                <Grid2 size={4}>
                    <RhfTextField
                        select
                        slotProps={sharedSlotProps}
                        name="crmStatus"
                        control={control}
                        label="Lead Status"
                        fullWidth
                        sx={sharedTextFieldSx}
                    >
                        {leadStatusOptionsWithNone.map((option) => (
                            <MenuItem key={option.label} value={String(option.id)}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </RhfTextField>
                </Grid2>
            </Grid2>

            <Grid2 container spacing={2} sx={{ mt: 3 }}>
                <Grid2 size={4}>
                    <RhfTextField
                        select
                        slotProps={sharedSlotProps}
                        name="litigationSubStatus"
                        control={control}
                        label="Substatus"
                        fullWidth
                        sx={sharedTextFieldSx}
                    >
                        {optionsLitigationSubStatusWithNone.map((option) => (
                            <MenuItem key={option} value={String(option)}>
                                {option}
                            </MenuItem>
                        ))}
                    </RhfTextField>
                </Grid2>
                <Grid2 size={4}>
                    <CaseReferredByCompanyAutocomplete />
                </Grid2>
                <Grid2 size={4}>
                    <CaseReferredByIndividualAutocomplete />
                </Grid2>
            </Grid2>
        </LocalizationProvider>
    );
};

export default DateRangeFields;
